import React from 'react';
import _ from 'lodash';
import { Button } from './Button';
import { Input } from './Input';
import { Message } from './Message';
import { Navigate } from './Navigate';
import { Select } from './Select';
import {
    __,
    formatAmount,
    request,
    getCheckoutItemName,
    isEmptyString,
    isValidZip,
} from '../functions';
import '../assets/styles/payment.css';
import {MARKET} from "../config";
import {CircularProgress, IconButton, Tooltip} from "@mui/material";
import ActivatedIcon from "@mui/icons-material/CheckCircle";
import SyncIcon from "@mui/icons-material/Sync";

/**
 * Payment komponenta.
 */
class Payment extends Navigate {
    static defaultProps = {
        user: {},
        market: '',
        type: '',
    };

    state = {
        loading: false,
        loading_vat_number: false,
        data: {},
        settings: {},
        valid_vat_number: false,
    };

    componentDidMount() {
        const { market, type } = this.props;

        this.fetch(market, type);
    }

    fetch(market, type) {
        const { onClose } = this.props;

        request(`/subscriptions/getPaymentData/${market}/${type}`).then(response => {
            const { status, data } = response.data;

            if (status === 'error') {
                onClose();
                return;
            }

            let settings = data.settings.invoicing;

            if (!_.has(settings, 'country') || settings.country === '') {
                settings = { ...settings, country: _.includes(data.markets, MARKET) ? MARKET : data.default_market };
            }

            let additional = {};

            if (_.isEmpty(this.state.settings)) {
                additional = { settings, valid_vat_number: data.valid_vat_number };
            }

            this.setState({ data, ...additional });
        });
    }

    onChangeSetting(name, value) {
        const { type } = this.props;
        const { settings } = this.state;

        let additional = {};

        switch (name) {
            case 'country':
                additional = { data: {} };
                break;

            case 'dic':
                additional = { valid_vat_number: false };
                break;
        }

        this.setState({ settings: { ...settings, [name]: value }, ...additional });

        if (name === 'country') {
            // Fetch new data
            this.fetch(value, type);
        }
    }

    validateVatNumber() {
        const { settings } = this.state;

        this.setState({ loading_vat_number: true });

        request(`/subscriptions/validateVatNumber/${settings.dic}`).then(response => {
            const { status } = response.data;

            if (status === 'error') {
                this.setState({
                    loading_vat_number: false,
                    valid_vat_number: false,
                });

                this.showSnackbar('error', __('DIČ sa nepodarilo overiť'));
                return;
            }

            this.setState({
                loading_vat_number: false,
                valid_vat_number: true,
            });
        });
    }

    isCheckVatNumber(settings) {
        const { data } = this.state;

        return settings.country !== 'sk' && _.includes(data.eu_countries, settings.country);
    }

    buy() {
        const { type, setUser, onClose } = this.props;
        const { settings, valid_vat_number } = this.state;

        let error = '';

        if (isEmptyString(settings.name)) {
            error = __('Nie je vyplnený názov');
        } else if (isEmptyString(settings.address)) {
            error = __('Nie je vyplnená adresa');
        } else if (isEmptyString(settings.city)) {
            error = __('Nie je vyplnené mesto');
        } else if (!isValidZip(settings.zip)) {
            // Neplatne PSC
            error = __('Neplatné PSČ');
        } else if (this.isCheckVatNumber(settings) && settings.dic !== '' && !valid_vat_number) {
            error = __('Neplatné DIČ');
        }

        if (error !== '') {
            this.showSnackbar('error', error);
            return null;
        }

        this.setState({ loading: true });

        request(`/subscriptions/createPayment/${type}`, { settings }, 'POST').then(response => {
            const { status, data, sync } = response.data;

            if (status === 'error') {
                this.showSnackbar('error', __('Nepodarilo sa vytvoriť platbu'));
                this.setState({ loading: false });
                return;
            }

            setUser(sync.user);

            if (_.has(data, 'id')) {
                request(`/subscriptions/pdf/${data.id}`).then(response => {
                    const { status, data } = response.data;

                    if (status === 'error') {
                        this.showSnackbar('error', __('Nepodarilo sa stiahnuť pdf'));
                        this.setState({ loading: false });
                        return;
                    }

                    // Stiahneme subor
                    window.location = data.link;

                    this.showSnackbar('success', __('Faktúra bola stiahnutá'));

                    setTimeout(() => onClose(), 2000);
                });

                return;
            }

            // Redirect
            window.location = data.link;
        });
    }

    /**
     * Rendrovanie.
     *
     * @return {JSX.Element}
     */
    render() {
        const { type } = this.props;
        const { data, loading, loading_vat_number, settings, valid_vat_number } = this.state;

        if (_.isEmpty(data)) {
            return <div
                className="payment-content payment-content-loading"
            >{this.renderLoading()}</div>;
        }

        const countries = Object.fromEntries(Object.entries(data.countries)
            .sort(([, valueA], [, valueB]) => valueA.localeCompare(valueB, 'sk', { sensitivity: 'base' })));

        return (
            <div className="payment-content">
                <div className="payment-content__data">
                    <div className="payment-content__data__title">{__('Fakturačné údaje')}</div>
                    <div className="payment-content__data__panels">
                        <div className="payment-content__data__panels__panel">
                            <Input
                                label={__('Adresa')}
                                value={settings.address}
                                onChange={value => this.onChangeSetting('address', value)}
                            />
                            <Input
                                label={__('Mesto')}
                                value={settings.city}
                                onChange={value => this.onChangeSetting('city', value)}
                            />
                            <Input
                                label={__('PSČ')}
                                value={settings.zip}
                                onChange={value => this.onChangeSetting('zip', value)}
                            />
                            <Select
                                label={__('Krajina')}
                                value={settings.country}
                                options={countries}
                                onChange={value => this.onChangeSetting('country', value)}
                                allowEmpty={false}
                            />
                        </div>
                        <div className="payment-content__data__panels__panel">
                            <Input
                                label={__('Názov')}
                                value={settings.name}
                                onChange={value => this.onChangeSetting('name', value)}
                            />
                            <Input
                                label={__('IČO')}
                                value={settings.ico}
                                onChange={value => this.onChangeSetting('ico', value)}
                            />
                            <div className="payment-content__data__panels__panel__vat-number">
                                <Input
                                    label={__('DIČ')}
                                    value={settings.dic}
                                    onChange={value => this.onChangeSetting('dic', value)}
                                />
                                {this.isCheckVatNumber(settings) ? <Tooltip title={__('Overiť')}>
                                    <span>
                                        <IconButton
                                            onClick={() => this.validateVatNumber()}
                                            className="order__content__sections__section__sync__button"
                                            disabled={valid_vat_number}
                                        >
                                            {loading_vat_number
                                                ? <CircularProgress className="loading" color="inherit" size={20} />
                                                : (valid_vat_number ? <ActivatedIcon color="secondary" /> : <SyncIcon />)}
                                        </IconButton>
                                    </span>
                                </Tooltip> : null}
                            </div>
                            {this.isCheckVatNumber(settings) && settings.dic !== '' && !valid_vat_number ? <Message type="warning">{__('Musíte overiť svoje DIČ')}</Message> : null}
                            {settings.country === 'sk' ? <Input
                                label={__('IČ DPH')}
                                value={settings.ic_dph}
                                onChange={value => this.onChangeSetting('ic_dph', value)}
                            /> : null}
                        </div>
                    </div>
                </div>
                <div className="payment-content__down">
                    <div className="payment-content__down__summary">
                        <div className="payment-content__down__summary__line">
                            <div className="payment-content__down__summary__line__name">{__('Názov')}:</div>
                            <div className="payment-content__down__summary__line__value">{getCheckoutItemName(type)}</div>
                        </div>
                        <div className="payment-content__down__summary__line">
                            <div className="payment-content__down__summary__line__name">{__('Celková suma')}:</div>
                            <div className="payment-content__down__summary__line__value">{formatAmount(data.price, data.currency)}</div>
                        </div>
                    </div>
                    <Button
                        onClick={() => this.buy()}
                        color="green"
                        loading={loading}
                    >{__('Objednať')}</Button>
                </div>
                {this.renderSnackbar()}
            </div>
        );
    }
}

export { Payment };
